import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from 'components/ui/use-toast';
import config from 'config';
import { useAuthContext } from 'modules/auth';
import { useUndoDelete } from 'modules/trash';
import type { BankQuestion } from './types';
import * as Api from './api';

export const QUERY_KEY = ['question-bank'] as const;
const TOPICS_QUERY_KEY = ['question-topics'] as const;

/**
 * `keepPrevious` keeps the last page on screen while the next one loads — for
 * paged tables only; a list that appends pages would append the old page again.
 */
export const useBankQuestionList = (
  params?: Api.ListBankQuestionsParams,
  { keepPrevious = false, enabled = true }: { keepPrevious?: boolean; enabled?: boolean } = {},
) => {
  return useQuery<Api.PaginatedBankQuestions>({
    queryKey: [...QUERY_KEY, params],
    enabled,
    queryFn: async () => {
      const res = await Api.List(params);
      return {
        data: res.data?.data ?? [],
        total: res.data?.total ?? 0,
        page: res.data?.page ?? 1,
        limit: res.data?.limit ?? 20,
        pages: res.data?.pages ?? 0,
      };
    },
    placeholderData: keepPrevious ? keepPreviousData : undefined,
  });
};

/** Derived from bank rows tagged with the draft's `sourceArtifactId` — no extra schema. */
export const useArtifactBankedStatus = (artifactId: string | undefined) => {
  return useQuery<{ banked: boolean; count: number; subjectId?: string }>({
    queryKey: [...QUERY_KEY, 'artifact-status', artifactId],
    enabled: Boolean(artifactId),
    queryFn: async () => {
      const res = await Api.List({ sourceArtifactId: artifactId, limit: 1 });
      const total = res.data?.total ?? 0;
      return {
        banked: total > 0,
        count: total,
        subjectId: res.data?.data?.[0]?.subjectId,
      };
    },
  });
};

/** Topics already used in the active org, for the editors' suggestion list. */
export const useQuestionTopics = () => {
  const { state } = useAuthContext();
  const orgId =
    (typeof localStorage !== 'undefined' ? localStorage.getItem(config.api.active_org_key) : null) ||
    state.organization?.id ||
    undefined;
  return useQuery<string[]>({
    queryKey: [...TOPICS_QUERY_KEY, orgId],
    enabled: Boolean(orgId),
    staleTime: 60_000,
    queryFn: async () => (await Api.ListTopics()).data?.data ?? [],
  });
};

export const useBankQuestionSingle = (id: string | undefined) => {
  return useQuery<BankQuestion | null>({
    queryKey: [...QUERY_KEY, 'single', id],
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) return null;
      const res = await Api.GetById(id);
      return res.data?.data ?? null;
    },
  });
};

export const useCreateBankQuestion = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: Api.CreateBankQuestionData): Promise<BankQuestion> => {
      const res = await Api.Create(data);
      return res.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TOPICS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['subjects'] });
      toast.success('Question added to bank');
    },
    onError: (error: any) => {
      toast.error('Error', {
        description: error?.response?.data?.message || 'Failed to create question',
      });
    },
  });
};

export const useUpdateBankQuestion = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Api.CreateBankQuestionData>;
    }): Promise<BankQuestion> => {
      const res = await Api.Update(id, data);
      return res.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TOPICS_QUERY_KEY });
      toast.success('Question updated');
    },
    onError: (error: any) => {
      toast.error('Error', {
        description: error?.response?.data?.message || 'Failed to update question',
      });
    },
  });
};

export const useDeleteBankQuestion = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const undoable = useUndoDelete();

  return useMutation({
    mutationFn: async (id: string) => {
      await Api.Delete(id);
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['subjects'] });
      undoable({ type: 'question', ids: [id], title: t('trash.deleted_question') });
    },
    onError: (error: any) => {
      toast.error('Error', {
        description: error?.response?.data?.message || 'Failed to delete question',
      });
    },
  });
};

export const useBulkCreateBankQuestions = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (items: Api.CreateBankQuestionData[]) => {
      const res = await Api.BulkCreate(items);
      return res.data?.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['subjects'] });
      toast.success(`${data?.created ?? 0} questions added to bank`);
    },
    onError: (error: any) => {
      toast.error('Error', {
        description: error?.response?.data?.message || 'Failed to add questions',
      });
    },
  });
};

/** Parse-only: the preview never writes, so there is nothing to invalidate. */
export const useImportPreview = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      file,
      subjectId,
      aiRepair,
      signal,
    }: {
      file: File;
      subjectId?: string;
      aiRepair?: boolean;
      signal?: AbortSignal;
    }) => {
      const res = await Api.PreviewImport(file, subjectId, { aiRepair, signal });
      return res.data?.data;
    },
    onError: (error: any) => {
      // Closing the dialog aborts the request on purpose; that is not a failure to report.
      if (error?.code === 'ERR_CANCELED') return;
      toast.error('Could not read the file', {
        description: error?.response?.data?.message || 'Check the format and try again',
      });
    },
  });
};

/** Platform-level switch for the import's AI pass; the toggle is hidden while it is off. */
export const useImportAiRepairStatus = (enabled: boolean) =>
  useQuery({
    queryKey: ['question-bank', 'import-ai-repair'],
    queryFn: async () => (await Api.GetImportAiRepairStatus()).data?.data,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const useDownloadImportTemplate = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (format: 'xlsx' | 'csv' | 'txt' | 'json' | 'docx' | 'pdf') => {
      const res = await Api.DownloadImportTemplate(format);
      const url = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `examing-question-template.${format}`;
      link.click();
      URL.revokeObjectURL(url);
    },
    onError: () => {
      toast.error('Could not download the template');
    },
  });
};

export const useBulkDeleteBankQuestions = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const undoable = useUndoDelete();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await Api.BulkDelete(ids);
      return res.data?.data;
    },
    onSuccess: (data, ids) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['subjects'] });
      const deleted = data?.deleted ?? 0;
      if (deleted === 0) {
        toast.success(t('trash.deleted_questions', { count: 0 }));
        return;
      }
      // The API does not say which ids it deleted; Undo skips any that were not.
      undoable({ type: 'question', ids, title: t('trash.deleted_questions', { count: deleted }) });
    },
    onError: (error: any) => {
      toast.error('Error', {
        description: error?.response?.data?.message || 'Failed to delete questions',
      });
    },
  });
};
